/**
 * ProfileIdentity — avatar below the banner + name / verify badge /
 * supporter badge / handle row, with the ProfileActions cluster to the right.
 *
 * There is exactly ONE paid mark on this row and it is `user.supporterBadge`
 * (server-derived: has_pro AND confirmed 2FA). The raw `user.premium` mirror is
 * never rendered here — an ungated "Premium" crown pill used to sit alongside
 * the badge and handed a visible paid mark to accounts the 2FA gate had
 * deliberately withheld it from. See features/profile/ProfileIdentity.paidMarks.test.tsx.
 */
import styled, { type DefaultTheme } from 'styled-components';
import { Avatar } from '@/ui';
import { Icon } from '@/icons';
import { ProfileActions } from './ProfileActions';
import type { User } from '@/core/models';
import { media } from '@/theme/media';

// ── Props ──────────────────────────────────────────────────────────────────────

export interface ProfileIdentityProps {
  user: User;
  isOwn: boolean;
  onEditProfile: () => void;
  onShareProfile: () => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const AVATAR_SIZE = 80;

// ── Styled ─────────────────────────────────────────────────────────────────────

const Container = styled.div`
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr);
  align-items: start;
  gap: 16px;
  ${media.mobile`
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 12px;
  `}
`;

/** 1.23.00 Phase 6: cosmetic flair colors, Pro-gated to render server-side
 * (user.flair is already 'none' for a non-Pro account — see
 * core/models/account.ts's User.flair). Purely decorative, invisible to
 * ranking; degrades harmlessly (just stops rendering) for a lapsed
 * subscriber, since the SERVER stops sending a real value, not this map.
 *
 * Reads THEME TOKENS, not hardcoded hexes: the original three hexes were
 * tuned for the dark surface only and washed out to ~2:1 in light theme.
 * theme.color.flair* carries a per-mode value (see theme/tokens.ts). */
function flairRingColor(theme: DefaultTheme, flair: string | undefined): string | undefined {
  switch (flair) {
    case 'aurora-ring':
      return theme.color.flairAurora;
    case 'ember-ring':
      return theme.color.flairEmber;
    case 'cyan-ring':
      return theme.color.flairCyan;
    default:
      return undefined;
  }
}

/** Flair remains decorative and follows the member's chosen corner radius. */
const AvatarHalo = styled.div<{ $flair?: string }>`
  display: flex;
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.radius.sm};
  ${({ theme, $flair }) => {
    const c = flairRingColor(theme, $flair);
    return c ? `box-shadow: 0 0 0 4px color-mix(in srgb, ${c} 18%, transparent);` : '';
  }}
  > [role='img'] {
    width: 100%;
    height: 100%;
    font-size: ${({ theme }) => theme.fontSize.display};
    line-height: ${({ theme }) => theme.lineHeight.display};
  }
  ${media.mobile`
    width: 56px;
    height: 56px;
    > [role='img'] {
      font-size: ${({ theme }) => theme.fontSize.xl};
      line-height: ${({ theme }) => theme.lineHeight.xl};
    }
  `}
`;

const IdentityHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  min-width: 0;
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  /* Long names and badges wrap within the content column at every width. */
  min-width: 0;
`;

const NameText = styled.h1`
  margin: 0;
  overflow-wrap: anywhere;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  line-height: ${({ theme }) => theme.lineHeight.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

/* The supporter badge's own colour token — NOT an accent token. `accentAmber`
   resolves to the very purple `accentCyan` does (the verified check's colour),
   which made the two marks colour-identical at 13–18px. See theme/tokens.ts. */
const SupporterBadgeGlyph = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.color.supporter};
`;

const HandleText = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.color.textMuted};
`;

// ── Component ──────────────────────────────────────────────────────────────────

export function ProfileIdentity({
  user,
  isOwn,
  onEditProfile,
  onShareProfile,
}: ProfileIdentityProps) {
  return (
    <Container>
      <AvatarHalo $flair={user.flair}>
        <Avatar name={user.name} size={AVATAR_SIZE} avatarKey={user.avatarKey} />
      </AvatarHalo>
      <IdentityHead>
        <NameBlock>
          <NameRow>
            <NameText>{user.name}</NameText>

            {user.verified && <Icon name="verify" size={18} title="Verified" />}

            {user.supporterBadge && (
              <SupporterBadgeGlyph>
                <Icon name="supporter-badge" size={16} title="Supporter" />
              </SupporterBadgeGlyph>
            )}
          </NameRow>

          <HandleText>{user.handle}</HandleText>
        </NameBlock>

        <ProfileActions
          user={user}
          isOwn={isOwn}
          onEditProfile={onEditProfile}
          onShareProfile={onShareProfile}
        />
      </IdentityHead>
    </Container>
  );
}
